import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  IncludeTestToggle,
  TestDataBanner,
  useIncludeTest,
  centsToUsd,
  pct,
  AwaitingBanner,
} from "./adminUi";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function AdminOverviewPage() {
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [health, setHealth] = useState({ proverb: null, system: null });

  useEffect(() => {
    setData(null);
    adminApi
      .masterOverview({ include_test: includeTest })
      .then(setData)
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Unable to load overview")
      );
  }, [includeTest]);

  // Lightweight parallel fetch for the Integration Health snapshot at the
  // top of the Overview. Uses the same endpoints powering the drill-down
  // pages so numbers agree. Failures degrade gracefully (fields stay null).
  useEffect(() => {
    Promise.allSettled([
      adminApi.proverbIntegrationStatus(),
      adminApi.systemHealth(),
    ]).then(([p, s]) => {
      setHealth({
        proverb: p.status === "fulfilled" ? p.value : null,
        system: s.status === "fulfilled" ? s.value : null,
      });
    });
  }, []);

  const lg = data?.lifeguide || {};
  const prv = data?.proverb || {};
  const sp = data?.sponsors || {};

  return (
    <div data-testid="admin-overview-page" className="space-y-8">
      <PageHeader
        eyebrow="LifeGuide Ecosystem"
        title="Master Dashboard"
        subtitle="Business performance, product engagement, sponsor impact, and cross-product growth."
        right={<IncludeTestToggle value={includeTest} onChange={setIncludeTest} />}
      />

      <TestDataBanner show={includeTest} />

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      {/* Integration Health snapshot — top-level, condensed */}
      <section data-testid="overview-integration-health">
        <SectionHeader
          label="Integration health"
          right={
            <Link
              to="/admin/system-health"
              className="text-xs text-[#B7D2AF] hover:text-white inline-flex items-center gap-1"
            >
              Full system health <ArrowUpRight size={12} />
            </Link>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ProverbHealthPill status={health.proverb} />
          <ServicesRollup services={health.system?.services} />
        </div>
      </section>

      {/* LifeGuide KPIs */}
      <section>
        <SectionHeader label="LifeGuide" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="ov-total-users" label="Total users" value={lg.total_users} />
          <Kpi testid="ov-new-7d" label="New — 7 days" value={lg.new_users_7d} />
          <Kpi testid="ov-new-30d" label="New — 30 days" value={lg.new_users_30d} />
          <Kpi testid="ov-active-7d" label="Active — 7 days" value={lg.active_users_7d} />
          <Kpi testid="ov-active-30d" label="Active — 30 days" value={lg.active_users_30d} />
          <Kpi testid="ov-free" label="Free users" value={lg.free_users} />
          <Kpi testid="ov-trials" label="Pro trials" value={lg.trial_users} />
          <Kpi testid="ov-paid" label="Paid Pro" value={lg.paid_pro} tone="accent" />
          <Kpi
            testid="ov-conv"
            label="Trial → Paid"
            value={lg.trial_to_paid_pct != null ? `${lg.trial_to_paid_pct}%` : "—"}
          />
          <Kpi testid="ov-gift-subs" label="Gift subscriptions" value={lg.gift_subscriptions} />
          <Kpi testid="ov-gifts-purchased" label="Gifts purchased" value={lg.gifts_purchased} />
          <Kpi testid="ov-guest-visitors" label="Guest visitors — 30d" value={lg.guest_visitors_30d} />
          <Kpi testid="ov-guest-conv" label="Guest → account — 30d" value={lg.guest_conversions_30d} tone="accent" />
          <Kpi
            testid="ov-mrr"
            label="MRR"
            value={centsToUsd(lg.mrr_cents)}
            tone="accent"
            hint="Per-subscription (webhook-maintained)"
          />
          <Kpi testid="ov-cancellations" label="Cancellations — 30 days" value={lg.cancellations_30d} />
        </div>
      </section>

      {/* Proverb KPIs */}
      <section>
        <SectionHeader
          label="I Need a Proverb"
          right={
            <Link
              to="/admin/proverb"
              data-testid="overview-proverb-link"
              className="text-xs text-[#B7D2AF] hover:text-white inline-flex items-center gap-1"
            >
              Open full analytics <ArrowUpRight size={12} />
            </Link>
          }
        />
        {prv.connected ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            <Kpi testid="ov-p-visitors" label="Visitors" value={prv.visitors} />
            <Kpi testid="ov-p-searches" label="Searches" value={prv.searches} />
            <Kpi testid="ov-p-results" label="Results viewed" value={prv.results_viewed} />
            <Kpi testid="ov-p-shares" label="Shares" value={prv.shares} />
            <Kpi testid="ov-p-daily-signups" label="Daily Proverb signups" value={prv.daily_signups} />
            <Kpi testid="ov-p-clicks" label="LifeGuide clicks" value={prv.lifeguide_clicks} />
            <Kpi testid="ov-p-refs" label="LifeGuide referrals" value={prv.lifeguide_referrals} />
            <Kpi testid="ov-p-signups" label="LifeGuide signups" value={prv.lifeguide_signups} />
            <Kpi testid="ov-p-trials" label="Trial starts" value={prv.trial_starts} />
            <Kpi testid="ov-p-paid" label="Paid Pro conversions" value={prv.paid_conversions} tone="accent" />
          </div>
        ) : (
          <AwaitingBanner label="I Need a Proverb analytics — awaiting integration" />
        )}
      </section>

      {/* Sponsors KPIs */}
      <section>
        <SectionHeader
          label="Sponsors"
          right={
            <Link
              to="/admin/sponsors"
              data-testid="overview-sponsors-link"
              className="text-xs text-[#B7D2AF] hover:text-white inline-flex items-center gap-1"
            >
              Manage Sponsors <ArrowUpRight size={12} />
            </Link>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="ov-sp-total" label="Total sponsors" value={sp.total} />
          <Kpi testid="ov-sp-active" label="Active sponsors" value={sp.active} />
          <Kpi testid="ov-sp-attr-users" label="Attributed users" value={sp.attributed_users} />
          <Kpi testid="ov-sp-attr-active" label="Attributed active" value={sp.attributed_active_users} />
          <Kpi testid="ov-sp-attr-paid" label="Attributed paid" value={sp.attributed_paid_users} tone="accent" />
          <Kpi testid="ov-sp-attr-gifts" label="Attributed gifts" value={sp.attributed_gifts} />
          <Kpi
            testid="ov-sp-avg-adopt"
            label="Avg. active adoption"
            value={sp.avg_active_adoption_rate == null ? "—" : `${sp.avg_active_adoption_rate}%`}
          />
          <Kpi
            testid="ov-sp-top"
            label="Top performer"
            value={sp.top_performer?.sponsor_name || "—"}
            hint={
              sp.top_performer
                ? `${sp.top_performer.paid} paid · ${sp.top_performer.active} active`
                : undefined
            }
          />
        </div>
      </section>

      {/* Cross-product funnel */}
      <section>
        <SectionHeader label="Cross-product funnel" />
        <Card>
          <FunnelStrip
            steps={[
              ["Proverb visitors", prv.visitors ?? null],
              ["LG clicks", prv.lifeguide_clicks ?? null],
              ["Signups", prv.lifeguide_signups],
              ["Trials", prv.trial_starts],
              ["Paid Pro", prv.paid_conversions],
            ]}
          />
        </Card>
      </section>
    </div>
  );
}

function FunnelStrip({ steps }) {
  return (
    <div className="grid grid-cols-5 gap-2 text-xs text-center" data-testid="ov-funnel-strip">
      {steps.map(([label, value], i) => {
        const prev = i > 0 ? steps[i - 1][1] : null;
        const rate = prev && value != null ? Math.round((value / prev) * 1000) / 10 : null;
        return (
          <div key={label} className="rounded-xl bg-[#0F1512] border border-white/10 p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#8FA894]">
              {label}
            </div>
            <div className="text-xl font-display text-white mt-1">
              {value == null ? <span className="text-[#8FA894] italic">Awaiting</span> : value}
            </div>
            {i > 0 && (
              <div className="text-[10px] text-[#7C8F7C] mt-0.5">
                {rate == null ? "—" : `${rate}% of previous`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// -- Integration Health helpers ---------------------------------------------
function ProverbHealthPill({ status }) {
  const healthy = !!status?.currently_healthy;
  const connected = !!status?.connected;
  const label = !status
    ? "Checking…"
    : healthy
    ? "Proverb — Healthy"
    : connected
    ? "Proverb — New failures since last success"
    : "Proverb — Awaiting integration";
  const tone = healthy
    ? "border-[#4F6F52]/40 text-[#8FBF87]"
    : connected
    ? "border-[#B4795C]/40 text-[#F3D6A6]"
    : "border-white/10 text-[#B7D2AF]";
  return (
    <Link
      to="/admin/proverb"
      data-testid="ov-proverb-health-card"
      className={`block rounded-2xl border ${tone} bg-[#141B18] p-4 hover:bg-[#1B2420] transition`}
    >
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#B7D2AF] mb-1">
        Proverb ingest
      </div>
      <div className="text-lg font-display leading-tight">{label}</div>
      {status && (
        <div className="mt-2 text-[11px] text-[#8FA894] flex flex-wrap gap-x-3 gap-y-1">
          <span>
            <span className="text-white">{status.event_count ?? 0}</span> prod events
          </span>
          <span>·</span>
          <span>
            <span className="text-white">{status.throughput?.last_24h ?? 0}</span> / 24h
          </span>
          <span>·</span>
          <span>
            Failures since last success:{" "}
            <span
              className={
                (status.failures_since_last_success ?? 0) === 0
                  ? "text-[#8FBF87]"
                  : "text-[#F3D6A6]"
              }
            >
              {status.failures_since_last_success ?? 0}
            </span>
          </span>
        </div>
      )}
    </Link>
  );
}

function ServicesRollup({ services }) {
  const order = [
    ["lifeguide", "LifeGuide"],
    ["database", "Database"],
    ["proverb_analytics_connection", "Proverb"],
    ["stripe", "Stripe"],
    ["resend", "Resend"],
    ["twilio", "Twilio (A2P ok)"],
  ];
  return (
    <Link
      to="/admin/system-health"
      data-testid="ov-services-rollup"
      className="block rounded-2xl border border-white/10 bg-[#141B18] p-4 hover:bg-[#1B2420] transition"
    >
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#B7D2AF] mb-2">
        System services
      </div>
      <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-[12px]">
        {order.map(([k, label]) => {
          const s = services?.[k];
          const status = s?.status || "unknown";
          const dot =
            status === "ok"
              ? "bg-[#8FBF87]"
              : status === "approved"
              ? "bg-[#8FBF87]"
              : status === "pending_a2p_review"
              ? "bg-[#F3D6A6]"
              : status === "awaiting_first_event" ||
                status === "configured_no_events"
              ? "bg-[#F3D6A6]"
              : status === "not_configured"
              ? "bg-[#B4795C]"
              : status === "down" || status === "degraded"
              ? "bg-[#E38680]"
              : "bg-white/20";
          return (
            <div
              key={k}
              className="flex items-center gap-2 min-w-0"
              data-testid={`ov-svc-${k}`}
              title={s?.note || status}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
              <span className="text-[#B7D2AF] truncate">{label}</span>
            </div>
          );
        })}
      </div>
    </Link>
  );
}

